import {List, Record} from 'immutable';
import NewFilterWord, {FilterWordId} from 'records/admin/topics/NewFilterWord';
import {Int, RecordProps} from '@store/@types/util';

export type FilterWordProfileId = Int;

/**
 * A filter word profile. Each profile is associated to any number of departments and/or teams.
 */
// XXX when upgrading typescript or immutable, double-check the casts here. Some record functions return immutable Maps
// instead of `this` for whatever reason. Or, maybe try to use our Data function to fix these problems.
export default class NewFilterWordProfile extends Record({
  filterWordProfileId: undefined,
  name: undefined,
  filterWords: List(),
}) {
  readonly filterWordProfileId: FilterWordProfileId;
  readonly name: string;
  readonly filterWords: List<NewFilterWord>;

  constructor(object: RecordProps<NewFilterWordProfile> = {filterWords: []}) {
    super({
      ...object,
      filterWords: List(object.filterWords!.map(filterWord => new NewFilterWord(filterWord))),
    });
  }

  /** Get just the topics from this profile. */
  topics(): List<NewFilterWord> {
    return this.filterWords.filter(fw => fw.topic);
  }

  /** Get just the keywords (non-topics) from this profile. */
  keywords(): List<NewFilterWord> {
    return this.filterWords.filterNot(fw => fw.topic);
  }

  /**
   * Return a copy of this profile with this profile's keywords replaced with the given keywords. Topics are
   * unchanged.
   *
   * @param newKeywords the keywords to set in the new profile.
   */
  replaceKeywords(newKeywords: List<NewFilterWord>): NewFilterWordProfile {
    const topics = this.topics(); // We'll keep these, of course.
    return this.set('filterWords', List([...topics, ...newKeywords]));
  }

  prependFilterWord(filterWord: NewFilterWord): NewFilterWordProfile {
    const toAdd = isNewFilterWordId(filterWord.filterWordId)
      ? filterWord.set('filterWordId', newFilterWordId(this.filterWords.size))
      : filterWord;
    return this.updateIn(['filterWords'], filterWords =>
      filterWords.unshift(toAdd)
    ) as NewFilterWordProfile; // annoying, but apparently necessary.
  }

  removeFilterWordById(filterWordId: FilterWordId): NewFilterWordProfile {
    return this.deleteIn([
      'filterWords',
      this.filterWords.findIndex(filterWord => filterWord.filterWordId === filterWordId),
    ]) as NewFilterWordProfile; // annoying, but apparently necessary.
  }

  updateFilterWord(updatedFilterWord: NewFilterWord): NewFilterWordProfile {
    return this.setIn(
      [
        'filterWords',
        this.filterWords.findIndex(
          filterWord => filterWord.filterWordId === updatedFilterWord.filterWordId
        ),
      ],
      updatedFilterWord
    );
  }

  cloneNew(): NewFilterWordProfile {
    // Copy without IDs, name.
    return this.delete('filterWordProfileId')
      .delete('name') // We'll force the user to enter a valid name.
      .updateIn(['filterWords'], filterWords =>
        filterWords.map((fw, idx) => (fw.topic ? fw : fw.set('filterWordId', newFilterWordId(idx))))
      ) as NewFilterWordProfile; // annoying, but apparently necessary.
  }

  sanitizeForServer(): NewFilterWordProfile {
    return this.updateIn(['filterWords'], filterWords =>
      filterWords
        .map(fw =>
          // remove fake ids used only on the client side for tracking of new filter words.
          isNewFilterWordId(fw.filterWordId) ? fw.delete('filterWordId') : fw
        )
        .map(fw =>
          // trim whitespace from ends of words before sending to server.
          fw.set('word', fw.word.trim())
        )
    ) as NewFilterWordProfile; // annoying, but apparently necessary.
  }
}

/**
 * Generate a new ID for a new filterWord when added to a profile.
 *
 * We need to be able to locate filter words by ID in the editor, so all filter words must have
 * one. Moreover, they need to follow a consistent pattern so that we can remove them before
 * sending to the server, which creates new entries in the database for filter words with null
 * IDs
 *
 * So we need a pattern. This is it.
 *
 * @param idx
 * @return a new filter word id.
 */
export function newFilterWordId(idx: Int): FilterWordId {
  return `new-${idx}`;
}

/**
 * Does the id given match the pattern for new ids above?
 * @param id the id to check.
 * @return true if id is undefined or is a string matching the pattern for new ids.
 */
export function isNewFilterWordId(id: FilterWordId | undefined): boolean {
  return !id || id.startsWith('new-');
}
